import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { isNewerVersion } from '../utils/versionUtils';
import { backendUrl } from '../constants';

const BACKEND_VERSIONS_URL = `${backendUrl}/app-versions/`;

// Dev mode: Add ?testUpdateDialog=true to URL to test the dialog in browser
const isDevTestMode = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('testUpdateDialog') === 'true';
};

type Platform = 'ios' | 'android' | 'web';

interface AppUpdateState {
  hasUpdate: boolean;
  isLoading: boolean;
  storeVersion: string | null;
  currentVersion: string | null;
  error: string | null;
  platform: Platform;
}

interface UseAppUpdateCheckReturn extends AppUpdateState {
  checkForUpdate: () => Promise<void>;
  dismissUpdate: () => void;
  isDialogVisible: boolean;
}

/**
 * Hook to check if a new version is available in the App Store (iOS) or Play Store (Android)
 * Shows dialog every time app opens if an update is available
 */
export const useAppUpdateCheck = (): UseAppUpdateCheckReturn => {
  const [state, setState] = useState<AppUpdateState>({
    hasUpdate: false,
    isLoading: false,
    storeVersion: null,
    currentVersion: null,
    error: null,
    platform: 'web',
  });
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  /**
   * Fetch store version from backend (primary source)
   * Backend fetches from iTunes API (iOS) and google-play-scraper (Android)
   */
  const fetchStoreVersionFromBackend = async (platform: Platform): Promise<string | null> => {
    try {
      const response = await fetch(BACKEND_VERSIONS_URL);
      if (!response.ok) {
        console.error('[AppUpdateCheck] Backend returned error:', response.status);
        return null;
      }
      const data = await response.json();
      const version = platform === 'ios' ? data.ios : data.android;
      console.log(`[AppUpdateCheck] Backend returned ${platform} version: ${version}`);
      return version || null;
    } catch (error) {
      console.error('[AppUpdateCheck] Error fetching version from backend:', error);
      return null;
    }
  };

  const getCurrentAppVersion = async (): Promise<string | null> => {
    try {
      const appInfo = await App.getInfo();
      return appInfo.version;
    } catch (error) {
      console.error('[AppUpdateCheck] Error getting current app version:', error);
      return null;
    }
  };

  const checkForUpdate = useCallback(async () => {
    const devMode = isDevTestMode();
    const platform = Capacitor.getPlatform() as Platform;

    // In dev test mode, simulate an update available
    if (devMode) {
      console.log('[AppUpdateCheck] Test mode enabled - simulating update available');
      setState({
        hasUpdate: true,
        isLoading: false,
        storeVersion: '99.0.0',
        currentVersion: '1.0.0',
        error: null,
        platform: Capacitor.isNativePlatform() ? platform : 'ios',
      });
      setIsDialogVisible(true);
      return;
    }

    // Only check on native platforms (iOS or Android)
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Only iOS and Android are supported
    if (platform !== 'ios' && platform !== 'android') {
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null, platform }));

    try {
      const storeVersion = await fetchStoreVersionFromBackend(platform);
      const currentVersion = await getCurrentAppVersion();

      if (!storeVersion || !currentVersion) {
        console.log('[AppUpdateCheck] Missing version info:', { storeVersion, currentVersion });
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Could not fetch version information',
        }));
        return;
      }

      const hasUpdate = isNewerVersion(storeVersion, currentVersion);
      console.log(
        `[AppUpdateCheck] Version check: store=${storeVersion}, current=${currentVersion}, hasUpdate=${hasUpdate}`,
      );

      setState({
        hasUpdate,
        isLoading: false,
        storeVersion,
        currentVersion,
        error: null,
        platform,
      });

      // Show dialog if update is available
      if (hasUpdate) {
        setIsDialogVisible(true);
      }
    } catch (error) {
      console.error('[AppUpdateCheck] Error checking for update:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Error checking for updates',
      }));
    }
  }, []);

  const dismissUpdate = useCallback(() => {
    setIsDialogVisible(false);
  }, []);

  // Check for updates when hook mounts (app opens)
  useEffect(() => {
    checkForUpdate();
  }, [checkForUpdate]);

  // Also check when app returns to foreground (iOS and Android)
  useEffect(() => {
    const platform = Capacitor.getPlatform();
    if (!Capacitor.isNativePlatform() || (platform !== 'ios' && platform !== 'android')) {
      return;
    }

    let listenerHandle: { remove: () => void } | null = null;
    let isMounted = true;

    const setupListener = async () => {
      const handle = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          checkForUpdate();
        }
      });
      if (isMounted) {
        listenerHandle = handle;
      } else {
        // Component unmounted before listener was set up, clean up immediately
        handle.remove();
      }
    };

    setupListener();

    return () => {
      isMounted = false;
      listenerHandle?.remove();
    };
  }, [checkForUpdate]);

  return {
    ...state,
    checkForUpdate,
    dismissUpdate,
    isDialogVisible,
  };
};
